import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal, viewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { VideoClip, WalkoutVideoClip } from '../../../../../types/review';
import { LossPreventionService } from '../../../../../service/loss-prevention.service';
import { PanVideoComponent } from '../../../../../components/pan-video/pan-video.component';
import { filter, interval, switchMap, take, takeUntil, timer } from 'rxjs';
import { NotifyService } from 'src/app/service/notify.service';

@Component({
  selector: 'app-review-video-clips',
  templateUrl: './review-video-clips.component.html',
  styleUrl: './review-video-clips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReviewVideoClipsComponent {
  private lossPreventionService = inject(LossPreventionService);
  private notifyService = inject(NotifyService);

  private destroyRef = inject(DestroyRef);

  public videoClips = input<Array<VideoClip | WalkoutVideoClip>>([]);
  public notUsefulSubmitted = input<boolean>(false);

  public report = output<{ videoClipId: string; clipNotUsefulReason: string }>();
  public expandedChange = output<boolean>();

  private panVideos = viewChildren(PanVideoComponent);

  public extendingClipId = signal<string | null>(null);
  private updatedLinks = signal<Record<string, string>>({});

  public getLinkForClip(videoClip: VideoClip | WalkoutVideoClip): string {
    return this.updatedLinks()[videoClip.id] ?? videoClip.link;
  }

  public onAddTimeBefore(videoClip: VideoClip | WalkoutVideoClip) {
    this.extendClip(videoClip, -30);
  }

  public onAddTimeAfter(videoClip: VideoClip | WalkoutVideoClip) {
    this.extendClip(videoClip, 30);
  }

  private extendClip(videoClip: VideoClip | WalkoutVideoClip, seconds: number) {
    const originalLink = this.getLinkForClip(videoClip);
    this.extendingClipId.set(videoClip.id);
    this.notifyService.notify('Downloading video segments...');

    this.lossPreventionService.extendVideoClip(videoClip.id, seconds).subscribe({
      next: () => {
        interval(10_000)
          .pipe(
            switchMap(() => this.lossPreventionService.getVideoClip(videoClip.id)),
            filter((clip) => clip.link !== originalLink),
            take(1),
            takeUntil(timer(5 * 60 * 1000)), // Stop polling after 5 minutes
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: (clip) => {
              this.updatedLinks.update((links) => ({ ...links, [videoClip.id]: clip.link }));
              this.extendingClipId.set(null);
              this.notifyService.notify('Video segments downloaded.');
              const index = this.videoClips().findIndex((c) => c.id === videoClip.id);
              this.panVideos()[index]?.reload();
            },
            error: () => {
              this.extendingClipId.set(null);
              this.notifyService.notify('Failed to check video status. Please try again.');
            },
            complete: () => {
              if (this.extendingClipId() === videoClip.id) {
                this.extendingClipId.set(null);
                this.notifyService.notify('Video extension timed out. Please try again.');
              }
            },
          });
      },
      error: () => {
        this.extendingClipId.set(null);
      },
    });
  }
}
